import { Injectable } from "@angular/core";
import { Address } from "@app/address/model/address";
import { customerRoutePaths } from "@app/customer/customer.routes";
import * as CustomerActions from "@app/customer/store/actions";
import { CustomerDbService } from "@app/customer/store/service/customer-db.service";
import { DataActions } from "@app/project/store/actions";
import { SnackbarActions } from "@app/root-store/actions";
import { EffectsUtils } from "@app/root-store/effects/effect-creators";
import { DeleteConfirmationService } from "@app/shared/components/delete-confirmation/delete-confirmation.service";
import { SendEmailDatabaseService } from "@app/shared/databases/email.database";
import { ActionWord } from "@app/shared/resources/action-word";
import { HttpResponseHandler } from "@app/shared/services/http-response-handler";
import { User } from "@app/user/model/user";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { of } from "rxjs";
import { catchError, exhaustMap, map, take } from "rxjs/operators";
import * as CustomerAction from "../actions";

const toAction = DataActions.toAction2();

const clareProduct = {
    name: "ClareHome",
};

const welcomeEmail = (
    inviteLink: string
) => `<p>Welcome to your Clare system,</p><br/><p>Your Clare integrator has invited you to access the ${clareProduct.name} mobile app, click the link below to get started.</p>
        <p>${window.location.protocol}//${window.location.host}${inviteLink}</p>

<p>Please note that you must use the email to which this email was sent to complete the process. Choose “Sign up with Email” to enter a unique password or either of the social sign in options. Please consult your Clare integrator if you have any questions.
</p>
        <p>Thanks, we're here to help,</p>
        <p>The Clare Team</p>`;

@Injectable()
export class CustomerEffects extends EffectsUtils {
    listRoutePath = `/${customerRoutePaths.root}${customerRoutePaths.list}`;

    createCustomer$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CustomerActions.create),
            exhaustMap(({ customer, callbackEmitter }) =>
                this.customerDbService
                    .checkForEmailExistence(customer)
                    .pipe(
                        exhaustMap(() =>
                            toAction(
                                this.customerDbService.createCustomer(customer),
                                CustomerActions.createSuccess,
                                CustomerActions.createFailure,
                                callbackEmitter
                            )
                        )
                    )
            ),
            catchError(DataActions.toError2(CustomerActions.createFailure))
        )
    );

    updateCustomer$ = this.createExhaustEffect({
        dbMethod: (payload: CustomerAction.ActionPayload) => this.customerDbService.updateCustomer(payload.customer, payload.logData),
        actionSet: CustomerActions.updateSet,
    });

    updateCustomerInviteCode$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CustomerActions.updateInviteCode),
            exhaustMap(({ customer }) =>
                toAction(
                    this.customerDbService.updateCustomer(customer).pipe(
                        map(({ customer: result }) => ({
                            recipientFirstName: result.firstName,
                            recipientLastName: result.lastName,
                            recipientEmail: result.email,
                            inviteCode: result.inviteCode,
                        }))
                    ),
                    CustomerActions.sendCustomerWelcomeEmail,
                    CustomerActions.updateInviteCodeFailure
                )
            ),
            catchError(DataActions.toError2(CustomerActions.updateFailure))
        )
    );

    deleteCustomer$ = this.createExhaustEffect({
        dbMethod: (payload: CustomerAction.ActionPayload) => this.customerDbService.deleteCustomer(payload.customer, payload.logData),
        actionSet: CustomerActions.deleteSet,
    });
    deleteCustomerConfirmation$ = this.createDeleteConfirmationEffect({
        actionSet: CustomerActions.deleteConfirmationSet,
        dbMethod: (payload: CustomerAction.ActionPayload) =>
            of({ payload: { customer: payload.customer, logData: payload.logData }, display: payload.customer.email || null }),
        confirmationService: this.deleteConfirmationService,
    });

    sendCustomerWelcomeEmail$ = this.createExhaustEffect({
        dbMethod: action => {
            const subject = `Get started with ${clareProduct.name}`;
            const body = welcomeEmail(
                `/signup?staff=false&email=${encodeURIComponent(action.recipientEmail)}&firstName=${encodeURIComponent(
                    action.recipientFirstName
                )}&lastName=${encodeURIComponent(action.recipientLastName)}&inviteCode=${action.inviteCode}`
            );
            return this.emailDbService.sendEmailRequest$(
                action.recipientEmail,
                action.recipientFirstName + " " + action.recipientLastName,
                subject,
                body
            );
        },
        actionSet: CustomerActions.sendCustomerWelcomeEmailSet,
    });

    sendCustomerModifiedLocationEmail$ = this.createExhaustEffect({
        dbMethod: action => {
            const subject = "Clare System Primary Customer Change";
            const body = `Hello,

            Your dealer has changed the primary customer on your ${clareProduct.name} account
            from <strong>${action.removedUser.firstName} ${action.removedUser.lastName}</strong>,
            <strong>${action.removedUser.email}</strong>
            to <strong>${action.newUser.firstName} ${action.newUser.lastName}</strong>.
            If you did not request this change please contact your dealer, <strong>${action.entityName}</strong>.

            You may ignore this email if this change was at your request.

            Kindest regards,
            The Clare Team`;
            return this.emailDbService.sendEmailRequest$(
                action.removedUser.email,
                action.removedUser.firstName + " " + action.removedUser.lastName,
                subject,
                body
            );
        },
        actionSet: CustomerActions.sendCustomerModifiedLocationEmailSet,
    });

    sendCustomerModifiedEmailFailure$ = this.createFailureEffect(CustomerActions.sendCustomerModifiedLocationEmailFailure, ActionWord.sent);

    sendCustomerMovedOutEmail$ = this.createExhaustEffect({
        dbMethod: action => {
            const subject = "Clare System Primary Customer Move Out Complete";
            const body = `Hello,

            Your property manager has completed the move out process and removed you as the primary customer on your ${clareProduct.name} account.
            If you did not request this change please contact your dealer, <strong>${action.entityName}</strong>.

            You may ignore this email if this change was at your request.

            Kindest regards,
            The Clare Team`;
            return this.emailDbService.sendEmailRequest$(
                action.removedUser.email,
                action.removedUser.firstName + " " + action.removedUser.lastName,
                subject,
                body
            );
        },
        actionSet: CustomerActions.sendCustomerMovedOutEmailSet,
    });

    sendCustomerMovedOutEmailFailure$ = this.createFailureEffect(CustomerActions.sendCustomerMovedOutEmailFailure, ActionWord.sent);

    sendCustomerMovedInEmail$ = this.createExhaustEffect({
        dbMethod: action => {
            const subject = "Clare System Primary Customer Move In Complete";
            const body = `Hello,

            <p>Your property manager has completed the Move-In process and added you as the primary customer on your ${clareProduct.name} account.
            If you do not recognize this change please contact your dealer, <strong>${action.entityName}</strong>.

            You may ignore this email if this change was at your request.<p>

            Kindest regards,<br/>
            The Clare Team`;
            const userName = `${action.user.firstName} ${action.user.lastName}`;
            return this.emailDbService.sendEmailRequest$(action.user.email, userName, subject, body);
        },
        actionSet: CustomerActions.sendCustomerMovedInEmailSet,
    });

    private sendCompanyAdminMoveInEmail(companyAdmins: User[], entityName: string, productName: string, userName: string) {
        if (companyAdmins?.length > 0) {
            const subject = "Clare System Primary Customer Move In Complete";
            const body = `Hello,

            <p>Your property manager has completed the Move-In process and added Mr./Ms. ${userName} as the primary customer on your ${productName} account.
            If you do not recognize this change please contact your dealer, <strong>${entityName}</strong>.

            You may ignore this email if this change was at your request.<p>

            Kindest regards,<br/>
            The Clare Team`;
            companyAdmins.forEach(admin => {
                const adminName = `${admin.firstName} ${admin.lastName}`;
                this.emailDbService.sendEmailRequest$(admin.email, adminName, subject, body).pipe(take(1)).subscribe();
            });
        }
    }

    private sendRequestToChangeMonitoringStatus(
        companyAdmins: User[],
        address: Address,
        companyName,
        isMoveIn: boolean,
        primaryName: string
    ) {
        if (companyAdmins?.length > 0) {
            /*content mail to admins when move in*/
            let subject = "Clare System Customer Move-In Complete";
            let body = `Dear <<admin_name>>,
            <p>${companyName} has completed the FusionPro Move-In process for the Clare system account for ${primaryName}, located at 
            ${address.address}, ${address.city}, ${address.state}. Please update account accordingly if central station monitoring services are required.<p>

            Kindest regards,<br/>
            The Clare Team`;

            if (!isMoveIn) {
                /*content mail to admins when move out*/
                subject = "Clare System Customer Move-Out Complete";
                body = `Dear <<admin_name>>,
                <p>${companyName} has completed the FusionPro Move-Out process for the Clare system account located at 
                ${address.address}, ${address.city}, ${address.state}. You may still need to cancel or suspend alarm monitoring service 
                with your central station if the account listed above is a monitored account.<p>
    
                Kindest regards,<br/>
                The Clare Team`;
            }
            companyAdmins.forEach(admin => {
                const adminName = `${admin.firstName} ${admin.lastName}`;
                this.emailDbService
                    .sendEmailRequest$(admin.email, adminName, subject, body.replaceAll("<<admin_name>>", adminName))
                    .pipe(take(1))
                    .subscribe();
            });
        }
    }

    sendCustomerMovedInEmailFailure$ = this.createFailureEffect(CustomerActions.sendCustomerMovedInEmailFailure, ActionWord.sent);
    sendCustomerMovedInSuccess$ = this.createSuccessEffect(CustomerActions.sendCustomerMovedInEmailSuccess, ActionWord.saved);

    createCustomerSuccess$ = this.createSuccessEffect(CustomerActions.createSuccess, ActionWord.saved);
    createCustomerFailure$ = this.createFailureEffect(CustomerActions.createFailure, ActionWord.saved);

    updateCustomerSuccess$ = this.createSuccessEffect(CustomerActions.updateSuccess, ActionWord.saved);
    updateCustomerFailure$ = this.createFailureEffect(CustomerActions.updateFailure, ActionWord.saved);

    deleteCustomerSuccess$ = this.createSuccessEffect(CustomerActions.deleteSuccess, ActionWord.deleted);
    deleteCustomerFailure$ = this.createFailureEffect(CustomerActions.deleteFailure, ActionWord.deleted);

    sendCustomerWelcomeEmailSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CustomerActions.sendCustomerWelcomeEmailSuccess),
            map(_ =>
                SnackbarActions.openSnackbar({
                    message: new HttpResponseHandler(null, null, `The customer welcome email was successfully sent.`),
                })
            )
        )
    );

    sendRequestChangeMonitoring$ = this.createExhaustEffect({
        dbMethod: action => {
            const primaryName = action.isMoveIn ? `${action.user.firstName} ${action.user.lastName}` : ``;
            const companyName = `${action.companyName}`;
            this.sendRequestToChangeMonitoringStatus(action.companyAdmins, action.address, companyName, action.isMoveIn, primaryName);
            return of(null);
        },
        actionSet: CustomerActions.sendRequestToChangeMonitoringSet,
    });

    sendRequestChangeMonitoringFailure$ = this.createFailureEffect(CustomerActions.sendRequestChangeMonitoringFailure, ActionWord.sent);
    sendRequestChangeMonitoringSuccess$ = this.createSuccessEffect(CustomerActions.sendRequestChangeMonitoringSuccess, ActionWord.saved);
    constructor(
        actions$: Actions,
        private deleteConfirmationService: DeleteConfirmationService,
        private customerDbService: CustomerDbService,
        private emailDbService: SendEmailDatabaseService
    ) {
        super(actions$);
    }
}
